import React, { useState, useEffect, useRef, PropsWithChildren } from "react";

const calculateWindowSize = () => {
  if (window.innerWidth >= 992) {
    return "desktop";
  }
  if (window.innerWidth >= 768) {
    return "tablet";
  }
  return "phone";
};

const getWindowSize = () => ({
  width: window.innerWidth || global.window.innerWidth,
  height: window.innerHeight || global.window.innerHeight,
  size: calculateWindowSize(),
});

type WithWindowSizeProps = {
  windowWidth: number;
  windowHeight: number;
  windowSize: string;
};

type UseWindowSizeProps = {
  width: number;
  height: number;
  size: string;
};

const WindowSizeContext = React.createContext<UseWindowSizeProps | null>(null);

function WindowSizeContextProvider({ children }: Readonly<PropsWithChildren>) {
  const [windowSize, setWindowSize] = useState(getWindowSize());
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const handleResize = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setWindowSize(getWindowSize());
      }, 100);
    };

    window.addEventListener("resize", handleResize, { signal });
    return () => {
      controller.abort();
    };
  }, []);

  return (
    <WindowSizeContext.Provider value={windowSize}>
      {children}
    </WindowSizeContext.Provider>
  );
}

const withWindowSize = <T extends WithWindowSizeProps = WithWindowSizeProps>(
  ChildComponent: React.ComponentType<T>,
) => {
  function ConnectedComponent(
    props: Readonly<Omit<T, keyof WithWindowSizeProps>>,
  ) {
    return (
      <WindowSizeContext.Consumer>
        {(context) => {
          // istanbul ignore next
          if (!context) {
            throw new Error(
              "withWindowSize must be used inside a WindowSizeContextProvider",
            );
          }
          return (
            <ChildComponent
              {...(props as T)}
              windowWidth={context.width}
              windowHeight={context.height}
              windowSize={context.size}
            />
          );
        }}
      </WindowSizeContext.Consumer>
    );
  }
  ConnectedComponent.displayName =
    ChildComponent.displayName ?? ChildComponent.name;
  return ConnectedComponent;
};

const useWindowSize = () => {
  const context = React.useContext(WindowSizeContext);
  // istanbul ignore next
  if (!context) {
    throw new Error(
      "useWindowSize must be used inside a WindowSizeContextProvider",
    );
  }
  return context;
};

export { WindowSizeContextProvider, withWindowSize, useWindowSize };
